// WP-1.15 (RE-4, NF-7, S-09 export): turn a filtered table into a CSV/XLSX download. The
// engine (exportTable.ts / xlsx.ts) owns the pure format generation; this file owns the
// web-only concerns: mapping the current view's rows to a `TabularData`, and triggering the
// browser download.
import { fromMinor, toCsv, toDecimalString, toXlsx, type TabularData } from '@pfos/engine';
import type { AccountResponse, TransactionResponse } from '@pfos/shared';

function accountName(accounts: AccountResponse[] | undefined, id: string): string {
  return accounts?.find((a) => a.id === id)?.name ?? id;
}

/** Signed major-unit amount (expense negative) as an exact decimal number — spreadsheet math
 * works, and `toDecimalString` keeps it exact from the integer minor units (never a float
 * round-trip on the money itself). */
function signedMajor(tx: TransactionResponse): number {
  const signedMinor = tx.type === 'expense' ? -tx.amountMinor : tx.amountMinor;
  return Number(toDecimalString(fromMinor(tx.currency, signedMinor)));
}

/** Build the S-09 transactions export table for whatever set of rows the filter is showing. */
export function buildTransactionsExport(
  transactions: TransactionResponse[],
  accounts: AccountResponse[] | undefined,
  filterSummary?: string,
): TabularData {
  const footnotes = [
    'Amounts in major currency units; expenses shown as negative.',
    'Valuation basis: actual recorded amounts — no indicative FX conversion applied (RE-4).',
    ...(filterSummary !== undefined && filterSummary !== '' ? [filterSummary] : []),
  ];
  return {
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'type', label: 'Type' },
      { key: 'category', label: 'Category' },
      { key: 'party', label: 'Party' },
      { key: 'account', label: 'Account' },
      { key: 'currency', label: 'Currency' },
      { key: 'amount', label: 'Amount' },
      { key: 'source', label: 'Source' },
      { key: 'note', label: 'Note' },
      { key: 'status', label: 'Status' },
    ],
    rows: transactions.map((tx) => ({
      date: tx.at.slice(0, 10),
      type: tx.type,
      category: tx.category ?? '',
      party: tx.party ?? '',
      account: accountName(accounts, tx.accountId),
      currency: tx.currency,
      amount: signedMajor(tx),
      source: tx.source,
      note: tx.note ?? '',
      status: tx.reversed ? 'reversed' : tx.adjustment === true ? 'adjustment' : '',
    })),
    footnotes,
  };
}

function triggerDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const UTF8_BOM = '﻿';

export function downloadCsv(filename: string, data: TabularData): void {
  // Prepend a UTF-8 BOM so Excel opens non-ASCII (e.g. the − sign, Urdu party names) correctly.
  const blob = new Blob([UTF8_BOM + toCsv(data)], { type: 'text/csv;charset=utf-8' });
  triggerDownload(filename, blob);
}

export function downloadXlsx(filename: string, sheetName: string, data: TabularData): void {
  // Copy into a fresh ArrayBuffer-backed view so the bytes satisfy BlobPart (the engine's
  // return type is Uint8Array<ArrayBufferLike>, which the DOM Blob typings reject directly).
  const bytes = new Uint8Array(toXlsx(sheetName, data));
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(filename, blob);
}
